<template>
  <login-wrap>
    <div class="login-email">
      <valid-form
        ref="email"
        @submit="onFormSubmit">
        <p
          v-if="isSSO && isSpecialLogin"
          class="email-special-title text-center">
          {{ string.emailSpecialTitle }}
        </p>
        <qx-input-row
          id="user-email"
          ref="inputRow"
          v-model="email"
          :label="string.emailLabel"
          validation-mode="passive"
          rules="required|email"
          name="userEmail"
          autocomplete="email"
          :trim="true"
          @keydown.space.prevent />
        <qx-button
          id="login"
          :is-block="true"
          :title="string.buttonLabel"
          :in-progress="inProgress"
          type="submit"
          state="active" />
      </valid-form>
      <div
        v-if="isSSO"
        class="sso-part">
        <p
          v-if="!isSpecialLogin"
          class="idp-label mt-0">
          {{ string.idpLabel }}
        </p>
        <idp-login-buttons />
      </div>
    </div>
  </login-wrap>
</template>

<script>
import { nextTick } from 'vue';
import { mapState, mapMutations, mapGetters } from 'vuex';
import { Form as ValidForm } from 'vee-validate';

// Elements
import QxInputRow from '@/elements/QxInputRow.vue';
import qxButton from '@/elements/button.vue';
import IdpLoginButtons from '@/elements/auth/IdpLoginButtons.vue';
import LoginWrap from '@/components/auth/LoginWrap.vue';

import { btoa } from '@/elements/utils.js';
import { EMAIL_CODE, PASSWORD_CODE } from '@/utils/cons.js';
import { signIn, shareLookup, returnFilesLookup, keyRequestLookup } from '@/api';
import { LOGIN_SSO } from '@/utils/cons';

const LOOKUPS = {
  download: shareLookup,
  'files-return': returnFilesLookup,
  'key-request': keyRequestLookup,
};

export default {
  name: 'LoginEmail',
  components: {
    QxInputRow,
    qxButton,
    IdpLoginButtons,
    ValidForm,
    LoginWrap,
  },
  emits: ['successLogin'],
  data() {
    return {
      inProgress: false,
      email: this.$store.state.auth.userEmail || '',
    };
  },
  computed: {
    ...mapState({
      userEmail: (state) => state.auth.userEmail,
      isStandAlonePage: (state) => state.isStandAlonePage,
      mfaCode: (state) => state.auth.mfaCode,
      idps: (store) => store.accountData.idps,
      metadataLoaded: (store) => store.metadataLoaded,
    }),
    ...mapGetters({
      isSpecialLogin: 'isSpecialLogin',
      getLoginSpecialData: 'getLoginSpecialData',
    }),
    string() {
      return {
        emailSpecialTitle: this.$gettext('Or'),
        emailLabel: this.$gettext('Email'),
        buttonLabel: this.getLoginSpecialData.loginBtn || this.$gettext('Log in'),
        idpLabel: this.$gettext('or log in with'),
      };
    },
    sourceLocation() {
      return encodeURIComponent(`${window.location.protocol}//${window.location.hostname}${window.location.pathname}`);
    },
    isSSO() {
      return this.idps?.length > 0;
    },
  },
  watch: {
    userEmail(val) {
      if (val === '') {
        this.inputFocus();
      }
      this.email = this.userEmail;
    },
  },
  methods: {
    ...mapMutations({
      setMfaCode: 'auth/setMfaCode',
      setLoginError: 'auth/setLoginError',
      setUserEmail: 'auth/setUserEmail',
    }),
    async onFormSubmit() {
      this.email = this.email.trim();
      await nextTick();
      this.loginAction();
    },
    async loginAction() {
      if (this.inProgress) {
        return;
      }
      this.inProgress = true;
      const tempToken = btoa(`${this.email.toLowerCase()}:`);
      this.setUserEmail(this.email);
      try {
        if (this.isStandAlonePage) {
          // Check if email can access share/request
          const lookupEndpoint = LOOKUPS[this.$route.name];

          await lookupEndpoint(this.$route.params.id, tempToken);

          // If email doesnt have access, just allow to input other mail
          if (this.mfaCode === EMAIL_CODE) {
            this.inProgress = false;
          }

          // If user need to input password we need first create session with email
          if (this.mfaCode === PASSWORD_CODE) {
            await signIn(tempToken);
          }
        } else {
          const { status } = await signIn(tempToken);
          if (status === 200) {
            this.$emit('successLogin');
            this.setLoginError(null);
          }
        }
        this.email = '';
      } catch (error) {
        if (error?.response?.data?.code === LOGIN_SSO) {
          if (this.idps.length === 1) {
            const idp = this.idps[0];
            // redirect to sso
            window.open(`${idp.url}?source=${this.sourceLocation}`, '_self');
          } else {
            this.setMfaCode(LOGIN_SSO);
          }
          return;
        }
        this.setLoginError('wrongEmail');
      } finally {
        this.inProgress = false;
      }
    },
    async inputFocus() {
      this.$refs.email.resetForm({
        values: { userEmail: '' },
        touched: { userEmail: false },
        errors: { userEmail: '' },
      });
      await this.$nextTick();
      this.$refs.inputRow.$el.querySelector('input[type=text]')?.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
.idp-label {
  color: $gray-700;
  margin-bottom: 20px;
  text-align: center;
}
</style>
